// apps/office/src/components/office/InteriorProjectForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Save, Home, MapPin, Calendar, Palette } from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const BHK_DURATION_DAYS = {
  '1 RK': 45,
  '1 BHK': 60,
  '2 BHK': 90,
  '3 BHK': 120,
  '4 BHK': 150,
  '5 BHK': 180,
  'Duplex': 180,
  'Office': 90,
  'Restaurant': 90,
  'Shop': 60,
  'Others': 90,
};

const DESIGN_STYLES = ['Standard Style', 'Luxury Style'];

const addDays = (dateStr, days) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const InteriorProjectForm = ({
  open, onOpenChange, existingProject, onSuccess, presetClientId,
}) => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!existingProject;

  const [clientId, setClientId] = useState('');
  const [propertyName, setPropertyName] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [quotationDate, setQuotationDate] = useState(new Date().toISOString().split('T')[0]);
  const [workStartDate, setWorkStartDate] = useState('');
  const [workEndDate, setWorkEndDate] = useState('');
  const [workEndManuallyEdited, setWorkEndManuallyEdited] = useState(false);
  const [designStyle, setDesignStyle] = useState('Standard Style');
  const [status, setStatus] = useState('Draft');
  const [notes, setNotes] = useState('');

  const { data: clients = [], isLoading: clientsLoading } = useQuery({
    queryKey: ['interior-clients-for-project'],
    queryFn: async () => {
      try {
        return await pb.collection('leads').getFullList({
          sort: '-created',
          filter: 'serviceType = "interior"',
        });
      } catch (err) {
        console.error('[InteriorProjectForm] clients fetch error:', err);
        return [];
      }
    },
    enabled: open && !presetClientId,
    staleTime: 30000,
  });

  /* ── Fetch preset client if provided ── */
  const { data: presetClient } = useQuery({
    queryKey: ['interior-client-preset', presetClientId],
    queryFn: () => pb.collection('leads').getOne(presetClientId),
    enabled: !!presetClientId && open,
  });

  const allClients = useMemo(() => {
    if (presetClient) {
      const found = clients.find((c) => c.id === presetClient.id);
      return found ? clients : [presetClient, ...clients];
    }
    return clients;
  }, [clients, presetClient]);

  const selectedClient = useMemo(
    () => allClients.find((c) => c.id === clientId) || null,
    [allClients, clientId]
  );

  useEffect(() => {
    if (!open) return;
    if (existingProject) {
      setClientId(existingProject.clientId || '');
      setPropertyName(existingProject.propertyName || '');
      setPropertyAddress(existingProject.propertyAddress || '');
      setQuotationDate(existingProject.quotationDate?.split('T')[0] || new Date().toISOString().split('T')[0]);
      setWorkStartDate(existingProject.workStartDate?.split('T')[0] || '');
      setWorkEndDate(existingProject.workEndDate?.split('T')[0] || '');
      setDesignStyle(existingProject.designStyle || 'Standard Style');
      setStatus(existingProject.status || 'Draft');
      setNotes(existingProject.notes || '');
      setWorkEndManuallyEdited(true);
    } else {
      setClientId(presetClientId || '');
      setPropertyName('');
      setPropertyAddress('');
      setQuotationDate(new Date().toISOString().split('T')[0]);
      setWorkStartDate('');
      setWorkEndDate('');
      setDesignStyle('Standard Style');
      setStatus('Draft');
      setNotes('');
      setWorkEndManuallyEdited(false);
    }
  }, [open, existingProject, presetClientId]);

  /* ── Auto-suggest property name + address ── */
  useEffect(() => {
    if (!selectedClient || isEditing) return;
    if (!propertyName && selectedClient.name) {
      setPropertyName(`${selectedClient.name}'s Property`);
    }
    if (!propertyAddress && selectedClient.address) {
      setPropertyAddress(selectedClient.address);
    }
  }, [selectedClient, isEditing]); // eslint-disable-line

  /* ── Auto-calculate workEndDate ── */
  useEffect(() => {
    if (workEndManuallyEdited) return;
    if (!workStartDate) { setWorkEndDate(''); return; }
    const pType = selectedClient?.propertyType || '2 BHK';
    const days = BHK_DURATION_DAYS[pType] || 90;
    setWorkEndDate(addDays(workStartDate, days));
  }, [workStartDate, selectedClient, workEndManuallyEdited]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        clientId,
        propertyName: propertyName.trim(),
        propertyAddress: propertyAddress.trim() || '',
        quotationDate: quotationDate || null,
        workStartDate: workStartDate || null,
        workEndDate: workEndDate || null,
        designStyle,
        status,
        notes: notes || '',
      };
      if (isEditing) {
        return pb.collection('interior_projects').update(existingProject.id, payload);
      }
      payload.createdBy = currentUser?.id || null;
      return pb.collection('interior_projects').create(payload);
    },
    onSuccess: () => {
      toast.success(isEditing ? '✅ Project updated' : '✅ Project created');
      queryClient.invalidateQueries(['interior-projects']);
      queryClient.invalidateQueries(['interior-projects-all']);
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (err) => {
      let msg = err.message || 'Save failed';
      if (err.originalError?.data?.message) msg = err.originalError.data.message;
      if (err.originalError?.data?.data) {
        const fields = Object.entries(err.originalError.data.data)
          .map(([f, e]) => `${f}: ${e.message}`).join('; ');
        if (fields) msg += ` → ${fields}`;
      }
      toast.error('❌ ' + msg);
    },
  });

  const handleSave = () => {
    if (!clientId) { toast.error('Please select a client'); return; }
    if (!propertyName.trim()) { toast.error('Property name is required'); return; }
    saveMutation.mutate();
  };

  const bhkLabel = selectedClient?.propertyType || '—';
  const autoDays = BHK_DURATION_DAYS[selectedClient?.propertyType] || 90;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Home className="w-5 h-5 text-emerald-600" />
            {isEditing ? 'Edit Interior Project' : 'New Interior Project'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label className="text-emerald-700 font-medium">Select Client *</Label>
            <Select
              value={clientId}
              onValueChange={setClientId}
              disabled={isEditing || !!presetClientId}
            >
              <SelectTrigger className="border-emerald-200">
                <SelectValue placeholder={clientsLoading ? 'Loading…' : 'Select interior client'}>
                  {(() => {
                    const sc = allClients.find((c) => c.id === clientId);
                    if (!sc) return clientsLoading ? 'Loading…' : 'Select interior client';
                    return <span>{sc.jobId ? `${sc.jobId} — ` : ''}{sc.name}</span>;
                  })()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} className="max-h-[280px] overflow-y-auto">
                {allClients.length === 0 ? (
                  <div className="p-3 text-xs text-slate-400 text-center">No interior clients found</div>
                ) : (
                  allClients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="font-mono text-xs text-emerald-600 mr-2">{c.jobId || '—'}</span>
                      {c.name}
                      {c.propertyType && (
                        <span className="text-[10px] text-slate-400 ml-2">({c.propertyType})</span>
                      )}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {selectedClient && (
              <p className="text-[11px] text-slate-500 mt-1">
                {selectedClient.phone || ''} {selectedClient.email ? `· ${selectedClient.email}` : ''}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-emerald-700 font-medium flex items-center gap-1.5">
              <Home className="w-3.5 h-3.5" /> Property Name *
            </Label>
            <Input
              value={propertyName}
              onChange={(e) => setPropertyName(e.target.value)}
              placeholder="e.g., Raj Apartment"
              className="border-emerald-200"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-emerald-700 font-medium flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> Property Address
            </Label>
            <Textarea
              rows={2}
              value={propertyAddress}
              onChange={(e) => setPropertyAddress(e.target.value)}
              placeholder="Street, area, city, pincode…"
              className="border-emerald-200"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-emerald-700 font-medium flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Quotation Date
              </Label>
              <Input type="date" value={quotationDate} disabled className="bg-slate-50 border-emerald-200" />
              <p className="text-[10px] text-slate-400">Auto-set to today</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-emerald-700 font-medium flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Work Start
              </Label>
              <Input
                type="date"
                value={workStartDate}
                onChange={(e) => setWorkStartDate(e.target.value)}
                className="border-emerald-200"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-emerald-700 font-medium flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Work End
              </Label>
              <Input
                type="date"
                value={workEndDate}
                onChange={(e) => {
                  setWorkEndDate(e.target.value);
                  setWorkEndManuallyEdited(true);
                }}
                className="border-emerald-200"
              />
              {!workEndManuallyEdited && workStartDate && (
                <p className="text-[10px] text-emerald-500">Auto: {bhkLabel} → {autoDays} days</p>
              )}
              {workEndManuallyEdited && (
                <button
                  type="button"
                  onClick={() => setWorkEndManuallyEdited(false)}
                  className="text-[10px] text-emerald-600 hover:underline"
                >
                  Reset to auto-calculate
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-emerald-700 font-medium flex items-center gap-1.5">
                <Palette className="w-3.5 h-3.5" /> Design Style *
              </Label>
              <Select value={designStyle} onValueChange={setDesignStyle}>
                <SelectTrigger className="border-emerald-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DESIGN_STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-emerald-700 font-medium">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="border-emerald-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-emerald-700 font-medium">Notes</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes…"
              className="border-emerald-200"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
          >
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEditing ? 'Update Project' : 'Create Project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InteriorProjectForm;
